import { Component, OnDestroy } from '@angular/core';
import { ProgressStep } from './progress-bar.model';
import { CompanyService } from '../company/company.service';
import { Router, NavigationEnd } from '@angular/router';
import { filter, Subject, takeUntil, combineLatest } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  isLockForValidationSelector,
  isPrfLocked,
  isValidationJobIdNotEmptySelector,
} from '../company/premiumreport/data-access/premium-report.selectors';
import {
  selectPrfNumber,
  selectPrfStatus,
  selectPrfYear,
} from '../store/selectors/app.selector';
import {
  selectAllFeedbackValid,
  selectValidationSummaryIsComplete,
} from '../company/validation-summary/data-access/validation-summary-selectors';
import { AppService } from '../app.service';

@Component({
  selector: 'app-progress-bar',
  templateUrl: './progress-bar.component.html',
  styleUrl: './progress-bar.component.scss',
  standalone: false,
})
export class ProgressBarComponent implements OnDestroy {
  currentRoute: string = '';
  currentYear: number;
  prfNumber: string;
  selectValidationSummaryIsComplete$;
  isLockedForValisation$ = this.store.select(isLockForValidationSelector);
  isValidationJobIdNotEmptySelector$ = this.store.select(
    isValidationJobIdNotEmptySelector
  );
  selectIsLockForValidation$ = this.store.select(isLockForValidationSelector);
  selectAllFeedbackValid$;
  IsValidationLocked: boolean = false;
  prfstatus: string = '';
  isReadOnlyAdmin: boolean = false;

  steps: ProgressStep[] = [
    {
      label: 'Company Information',
      route: '/companyinformation',
      completed: false,
      disabled: true,
    },
    {
      label: 'Group Reporting',
      route: '/groupreporting',
      completed: false,
      disabled: true,
    },
    {
      label: 'Annual Statement',
      route: '/annualstatement',
      completed: false,
      disabled: true,
    },
    {
      label: 'Premium Report',
      route: '/premiumreport',
      completed: false,
      disabled: true,
    },
    {
      label: 'User Defined Data',
      route: '/userdefineddata',
      completed: false,
      disabled: true,
    },
    { label: 'Comments', route: '/comments', completed: false, disabled: true },
    {
      label: 'Validation Summary',
      route: '/validationsummary',
      completed: false,
      disabled: true,
    },
    {
      label: 'Complete',
      route: '/PRFSubmissionComplete',
      completed: false,
      disabled: true,
    },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor(
    private companyService: CompanyService,
    private router: Router,
    private store: Store,
    private appService: AppService
  ) {}

  ngOnInit() {
    // Set current step as enabled on initial load
    this.setCurrentStepEnabled(this.router.url);
    this.isReadOnlyAdmin = this.appService.isReadonlyAdmin;

    this.store.select(selectPrfYear).subscribe((year) => {
      this.currentYear = year;
    });

    this.store.select(selectPrfNumber).subscribe((prfNumber) => {
      this.prfNumber = prfNumber || '';
    });

    this.selectAllFeedbackValid$ = this.store.select(selectAllFeedbackValid);
    this.selectValidationSummaryIsComplete$ = this.store.select(
      selectValidationSummaryIsComplete
    );
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.currentRoute = event.url;
        // Ensure current step is never disabled
        this.setCurrentStepEnabled(event.url);
      });

    // Company Information
    this.companyService.companyInformationCompleted.subscribe((completed) => {
      this.steps[0].completed = completed;
      this.steps[0].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.companyInformationInProgress.subscribe((inProgress) => {
      this.steps[0].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });

    // Group Reporting
    this.companyService.groupreportingCompleted.subscribe((completed) => {
      this.steps[1].completed = completed;
      this.steps[1].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.groupreportingInProgress.subscribe((inProgress) => {
      this.steps[1].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });

    // Annual Statement
    this.companyService.annualstatementCompleted.subscribe((completed) => {
      this.steps[2].completed = completed;
      this.steps[2].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.annualstatementInProgress.subscribe((inProgress) => {
      this.steps[2].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.annualstatementBlocked.subscribe((blocked) => {
      if (blocked) {
        this.steps[2].disabled = true;
        this.steps[2].completed = false;
      }
      this.setCurrentStepEnabled(this.router.url);
    });

    // Premium Report
    this.companyService.premiumreportCompleted.subscribe((completed) => {
      this.steps[3].completed = completed;
      this.steps[3].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.premiumreportInProgress.subscribe((inProgress) => {
      this.steps[3].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.premiumreportBlocked.subscribe((blocked) => {
      if (blocked) {
        this.steps[3].disabled = true;
        this.steps[3].completed = false;
      }
      this.setCurrentStepEnabled(this.router.url);
    });

    // User Defined Data
    this.companyService.userdefineddataCompleted.subscribe((completed) => {
      this.steps[4].completed = completed;
      this.steps[4].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.userdefineddataInProgress.subscribe((inProgress) => {
      this.steps[4].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.userdefineddataBlocked.subscribe((blocked) => {
      if (blocked) {
        this.steps[4].disabled = true;
        this.steps[4].completed = false;
      }
      this.setCurrentStepEnabled(this.router.url);
    });

    // Centralize the logic for step 6 (Validation Summary)
    this.isValidationJobIdNotEmptySelector$
      .pipe(takeUntil(this.destroy$))
      .subscribe((hasValidationJob) => {
        console.log("progress bar validation job", hasValidationJob);
        this.steps[6].disabled = !hasValidationJob; // Enable step 6 if hasValidationJob is true
        this.steps[6].completed = hasValidationJob; // Mark step 6 as completed if hasValidationJob is true
      });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        // Ensure step 6 remains enabled if hasValidationJob is true
        this.isValidationJobIdNotEmptySelector$
          .pipe(takeUntil(this.destroy$))
          .subscribe((hasValidationJob) => {
            if (hasValidationJob) {
              this.steps[6].disabled = false;
              this.steps[6].completed = true;
            }
          });
      });

    // Comments
    this.companyService.commentsCompleted.subscribe((completed) => {
      this.steps[5].completed = completed;
      this.steps[5].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.commentsInProgress.subscribe((inProgress) => {
      this.steps[5].disabled = false;
      this.setCurrentStepEnabled(this.router.url);
    });
    this.companyService.commentsBlocked.subscribe((blocked) => {
      if (blocked) {
        this.steps[5].disabled = true;
        this.steps[5].completed = false;
      }
      this.setCurrentStepEnabled(this.router.url);
    });

    // Global disable
    this.companyService.disablePrf.subscribe((disable) => {
      this.steps.forEach((step) => (step.disabled = disable));
      this.setCurrentStepEnabled(this.router.url);
    });
    combineLatest([
      this.store.select(isLockForValidationSelector),
      this.store.select(isPrfLocked),
      this.store.select(selectAllFeedbackValid),
      this.store.select(selectValidationSummaryIsComplete),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([
          isValidationLocked,
          prfLocked,
          selectAllFeedbackValid,
          selectValidationSummaryIsComplete,
        ]) => {
          this.IsValidationLocked = isValidationLocked;
          if (this.router.url.includes('/validationsummary')) {
            console.log("validationsummary",isValidationLocked,prfLocked,selectAllFeedbackValid,selectValidationSummaryIsComplete);
            const isCompleteEnabled =
              !isValidationLocked &&
              selectAllFeedbackValid &&
              prfLocked &&
              selectValidationSummaryIsComplete;
            this.steps[7].completed = isCompleteEnabled; // Mark "Complete" step as completed
            this.steps[7].disabled = !isCompleteEnabled; // Enable "Complete" step only if the condition is met
          } else {
            // Enable or disable "Complete" step based on isValidationLocked and prfLocked
           
            const isCompleteEnabled = !isValidationLocked && prfLocked;
            console.log("else",isValidationLocked,prfLocked);
            this.steps[7].completed = isCompleteEnabled;
            this.steps[7].disabled = !isCompleteEnabled;
          }
        }
      );
    this.store
      .select(selectPrfStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        if (status) {
          console.log('prfstatus in progress bar', status);
          this.prfstatus = status;

          // Disable all steps if prfstatus is "notStarted"
          this.setAllStepsDisabledWhenNotStarted();
        }
      });
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.isValidationJobIdNotEmptySelector$
          .pipe(takeUntil(this.destroy$))
          .subscribe((hasValidationJob) => {
            this.steps[6].disabled = !hasValidationJob; // Enable step 6 if hasValidationJob is true
            this.steps[6].completed = hasValidationJob; // Mark step 6 as completed if hasValidationJob is true
            if (hasValidationJob) {
              this.setCurrentStepEnabled(this.router.url); // Ensure step 6 is enabled
            }
          });
      });
  }

  ngOnDestroy(): void {
    // Unsubscribe when the component is destroyed
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setCurrentStepEnabled(url: string) {
    this.steps.forEach((step) => {
      if (url.includes(step.route)) {
        step.disabled = false;
      }
    });
  }

  getStepClass(step: ProgressStep): any {
    const isCurrent = this.router.url.includes(step.route);
    this.setAllStepsDisabledWhenNotStarted();
    return {
      completed: step.completed,
      // Only apply disabled style if step is disabled and NOT current
      disabled: step.disabled && !isCurrent,
      clickable: this.isStepClickable(step),
      current: isCurrent && !step.completed,
      active: isCurrent,
    };
  }

  isStepClickable(step: ProgressStep): boolean {
    return step.completed;
  }

  navigateToStep(step: ProgressStep) {
    if (this.isStepClickable(step)) {
      this.router.navigate([
        `/${this.currentYear}/${this.prfNumber}${step.route}`,
      ]);
    }
  }

  navigateToPrevStep() {
    const currentIndex = this.steps.findIndex((step) =>
      this.router.url.includes(step.route)
    );
    if (currentIndex > 0) {
      // Retain the specified conditions
      if (currentIndex === 2 && !this.isgroupbullet()) {
        this.router.navigate([
          `/${this.currentYear}/${this.prfNumber}${this.steps[0].route}`,
        ]);
        return;
      }
      if (currentIndex - 1 === 1 && !this.isgroupbullet()) {
        this.router.navigate([
          `/${this.currentYear}/${this.prfNumber}${this.steps[0].route}`,
        ]);
        return; // Skip "Group Reporting" if it is not on
      }

      // Find the last completed step before the current one
        this.router.navigate([
          `/${this.currentYear}/${this.prfNumber}${this.steps[currentIndex - 1].route}`,
        ]);
    }
  }

  navigateToNextStep() {
    const currentIndex = this.steps.findIndex((step) =>
      this.router.url.includes(step.route)
    );
    if (currentIndex === 5 && this.steps[6].completed) {
      // Navigate to "Validation Summary" from "Comments"
      this.router.navigate([
        `/${this.currentYear}/${this.prfNumber}${this.steps[6].route}`,
      ]);
      return;
    }
    if (currentIndex == 0 && !this.isgroupbullet()) {
      this.router.navigate([
        `/${this.currentYear}/${this.prfNumber}${this.steps[2].route}`,
      ]);
      return;
    }
    if (this.steps[currentIndex].completed) {
      this.router.navigate([
        `/${this.currentYear}/${this.prfNumber}${
          this.steps[currentIndex + 1].route
        }`,
      ]);
    }
  }

  isPrevButtonEnabled(): boolean {
    const currentIndex = this.steps.findIndex((step) =>
      this.router.url.includes(step.route)
    );
    // Enable "Prev" button for "Annual Statement" if "Group Reporting" is not on
    if (currentIndex === 2 && !this.isgroupbullet()) {
      return true;
    }
    if (currentIndex === 6) {
      return true;
    }
    return (
      currentIndex > 0 &&
      (this.steps[currentIndex - 1].completed || currentIndex - 1 === 0)
    );
  }

  isNextButtonEnabled(): boolean {
    // Retrieve the latest prfstatus value
    this.store.select(selectPrfStatus).subscribe((status) => {
      this.prfstatus = status;
    });

    if (this.prfstatus === 'notStarted') {
      return false; // Disable "Next" button if prfstatus is "notStarted"
    }
    

    const currentIndex = this.steps.findIndex((step) =>
      this.router.url.includes(step.route)
    );
    if(this.IsValidationLocked && currentIndex ===6){
          return false;
        }
    if (currentIndex !== -1 && currentIndex !== undefined) {
      // Helper to check if all steps before Comments are completed
    const allCompletedBeforeComments = this.steps
      .slice(0, 5)
      .every((step, index) => {
        if (index === 1) {
          // "Group Reporting" step
          return this.isgroupbullet() ? step.completed : true;
        }
        return step.completed;
      });
      const isCommentsStep = currentIndex === 5;
      const isValidationSummaryStep = currentIndex === 6;

      if (isCommentsStep) {
        return this.isReadOnlyAdmin
          ? allCompletedBeforeComments && this.steps[6].completed
          : allCompletedBeforeComments || this.steps[6].completed;
      }

      if (isValidationSummaryStep) {
        return this.isReadOnlyAdmin
          ? allCompletedBeforeComments && this.steps[7].completed
          : allCompletedBeforeComments && this.steps[6].completed;
      }


      return (
        currentIndex < this.steps.length - 1 &&
        this.steps[currentIndex].completed
      );
    }

    return false;
  }
  isgroupbullet() {
    return true;
  }

  getNextButtonLabel(): string {
    const currentIndex = this.steps.findIndex((step) =>
      this.router.url.includes(step.route)
    );
    const allCompletedBeforeComments = this.steps
      .slice(0, 5)
      .every((step, index) => {
        if (index === 1) {
          // Index 1 corresponds to "Group Reporting"
          return this.isgroupbullet() ? step.completed : true;
        }
        return step.completed;
      });
    if (currentIndex === 5) {
      // Index 5 corresponds to "Comments"
      if (this.steps[6].completed && this.IsValidationLocked) {
        // Step 6 corresponds to "Validation Summary"
        return 'Next'; // Navigate to "Validation Summary"
      }
      if (allCompletedBeforeComments && !this.IsValidationLocked && !this.steps[7].completed) {
        return this.isReadOnlyAdmin ? 'Next' : 'Submit Report';
      }
    }
    if (currentIndex === 6) {
      if (
        this.steps[6].completed &&
        !this.steps[7].completed
      ) {
        return this.isReadOnlyAdmin ? 'Next' : 'Submit Report';
      }
    }
    return 'Next';
  }
  openFinalSubmission() {
    this.router.navigate([
      `/${this.currentYear}/${this.prfNumber}/PRFSubmission`,
    ]);
  }
  setAllStepsDisabledWhenNotStarted() {
    if (this.prfstatus === 'notStarted') {
      this.steps.forEach((step) => {
        step.disabled = true;
        step.completed = false;
      });
    }
  }
}